// The option lists behind the criteria controls, loaded once per screen that
// asks for them (owner, 2026-09-14 — the Filters button).
//
// Analytics → Reporting and Analytics → Dashboard ask the same sixteen
// questions (`lib/criteria.ts`), so they need the same five option lists and the
// same vendor search. This hook is that half: `criteria.ts` holds the vocabulary
// and the conversions, this holds the reads.
//
// EACH READ SETTLES INDEPENDENTLY. A picker that could not load its options is
// not a reason to refuse the screen — every other criterion still works, and the
// whole form is optional.

import { useCallback, useEffect, useMemo, useState } from 'react'
import type { MultiSelectOption } from '../components/MultiSelectField'
import { FLAG_OPTIONS } from './criteria'
import {
  loadAllCodes,
  searchCompanies,
  searchDepartments,
  searchAccounts,
  searchVendors,
} from './data'

interface CriteriaOptions {
  companies: MultiSelectOption[]
  /** The Accounts picker's options. */
  accounts: MultiSelectOption[]
  /** The Departments picker's options. */
  departments: MultiSelectOption[]
  /** The Codes picker's options (whole codes). */
  codes: MultiSelectOption[]
  /** The vendor picker's server search. A stable reference, as
      `MultiSelectField` requires. */
  searchVendorOptions: (term: string) => Promise<MultiSelectOption[]>
  /** Display names for the ids a criteria object holds — companies, vendors
      seen while searching, and flags. What `describeReportCriteria` prints. */
  labels: {
    companies: Record<string, string>
    vendors: Record<string, string>
    flags: Record<string, string>
  }
}

/**
 * `companyIds`, when given, narrows the Company picker to those companies: an
 * Analytics tab offers only the companies its seats name (D-258), since any
 * other would come back empty. The label map keeps every name either way.
 */
export function useCriteriaOptions(companyIds?: readonly string[]): CriteriaOptions {
  const [companies, setCompanies] = useState<MultiSelectOption[]>([])
  const [accounts, setAccounts] = useState<MultiSelectOption[]>([])
  const [departments, setDepartments] = useState<MultiSelectOption[]>([])
  const [codes, setCodes] = useState<MultiSelectOption[]>([])

  useEffect(() => {
    let live = true
    void (async () => {
      const settle = async <T,>(work: Promise<T>, fallback: T): Promise<T> => {
        try {
          return await work
        } catch {
          return fallback
        }
      }
      const [companyRows, accountRows, departmentRows, codeRows] = await Promise.all([
        settle(searchCompanies(''), []),
        settle(searchAccounts(''), []),
        settle(searchDepartments(''), []),
        settle(loadAllCodes(), []),
      ])
      if (!live) return
      const byLabel = (a: MultiSelectOption, b: MultiSelectOption) => a.label.localeCompare(b.label)
      const byId = (a: MultiSelectOption, b: MultiSelectOption) => a.value.localeCompare(b.value)
      setCompanies(
        companyRows.map((c) => ({ value: c.company_id, label: c.company_name })).sort(byLabel),
      )
      // The three masters show ID — NAME and sort by id, because a criterion
      // has to be recognizable both ways: staff say "61-000" and the chart says
      // "Office Supplies".
      setAccounts(
        accountRows
          .map((a) => ({ value: a.account_id, label: `${a.account_id} — ${a.account_name}` }))
          .sort(byId),
      )
      setDepartments(
        departmentRows
          .map((d) => ({ value: d.department_id, label: `${d.department_id} — ${d.department_name}` }))
          .sort(byId),
      )
      setCodes(
        codeRows
          .map((c) => ({ value: c.code_id, label: `${c.code_id} — ${c.code_name}` }))
          .sort(byId),
      )
    })()
    return () => {
      live = false
    }
  }, [])

  // Names for the vendors the user has seen while searching. Vendor names are
  // not held as an option list (see below), so this is what lets a cover page or
  // a filter chip print a name rather than an id.
  const [vendorNames, setVendorNames] = useState<Record<string, string>>({})

  /**
   * VENDORS ARE SEARCHED ON THE SERVER, not held. There are 6,210 and
   * `MAX_PAGE_LIMIT` is 200, so loading "the list" would offer 200 of them and
   * silently omit the rest — a vendor the user knows exists simply missing from
   * the picker. Every other list here fits and is held.
   */
  const searchVendorOptions = useCallback(async (term: string): Promise<MultiSelectOption[]> => {
    const rows = (await searchVendors(term, 200))
      .map((v) => ({ value: v.vendor_id, label: v.vendor_name }))
      .sort((a, b) => a.label.localeCompare(b.label))
    // Remember every name seen, so a name can still be printed for a vendor
    // picked three searches ago. Nothing else holds it: the criteria carry
    // vendor ids, and the option that supplied the name has left the listing.
    setVendorNames((prev) => {
      const next = { ...prev }
      for (const row of rows) next[row.value] = row.label
      return next
    })
    return rows
  }, [])

  const labels = useMemo(
    () => ({
      companies: Object.fromEntries(companies.map((c) => [c.value, c.label])),
      vendors: vendorNames,
      flags: Object.fromEntries(FLAG_OPTIONS.map((f) => [f.value, f.label])),
    }),
    [companies, vendorNames],
  )

  const allowed = companyIds === undefined ? null : companyIds.join('\u0000')
  const offeredCompanies = useMemo(() => {
    if (allowed === null) return companies
    const keep = new Set(allowed.split('\u0000'))
    return companies.filter((c) => keep.has(c.value))
  }, [companies, allowed])

  return { companies: offeredCompanies, accounts, departments, codes, searchVendorOptions, labels }
}
